import React, {useState, useEffect, useRef, useMemo} from 'react'
import {Row} from '@bedrock/components'
import { create } from 'zustand';
import { SFormItem,
  configFunctionToValue,
  SFormItemProps,
} from "#components/SForm/SFormItem";
import DescriptionsFormItem from "../DescriptionsFormItem";
import {DescriptionsItemType} from "@bedrock/components/lib/Descriptions/Item";
import cx from 'classnames'
import './index.less'
import {SFormComponentClass} from "#components/SForm/SFormElement";


// demo，切换布局
export const useFormConfigStore = create<{isRow: boolean}>((set, get) => ({
    isRow: true,
}))

type FormItemProp<U extends SFormComponentClass = SFormComponentClass> = Omit<SFormItemProps<string,U>, 'formItemKey'> & {
  formItemKey?: string;
  descriptionItemProp?: Omit<DescriptionsItemType, 'label' | 'children' | 'span'>;
  componentClass?: SFormComponentClass,
}

type GlobalFormItemProps = Omit<SFormItemProps, 'formItemKey' | 'componentClass' | 'elementProps'>;

type FormItemMapProp = Record<string, FormItemProp>

type FormItemColType<T extends FormItemMapProp> =
    |(keyof T)
    | {
      [K in keyof T]: {formItemKey: K} & FormItemProp
    }[keyof T];

type FormItemRowProp<T extends FormItemMapProp = FormItemMapProp> = {
  formItemMap?: T;
  renderType?: 'row' | 'descriptions';
  renderData?: {
    formData?: any;
    detailData?: any;
    optionMap?: Record<string, ({name: string, value: string} & any)[]>;
  };
  itemCols: FormItemColType<T>[];
  globalItemProps?: GlobalFormItemProps;
  row?: React.ComponentProps<typeof Row>
  descriptionsProps?: Omit<React.ComponentProps<typeof DescriptionsFormItem>, 'items'>;
}

// 将对象的控制清空
function clearObject(obj: any) {
  return Object.keys(obj || {}).reduce((acc, key) => {
    if (obj[key] !== undefined) {
      acc[key] = obj[key]
    }
    return acc
  }, {})
}

export function FormItemRow<T extends FormItemMapProp>({
  renderType,
  formItemMap,
  renderData,
  itemCols,
  globalItemProps = {},
  row,
  descriptionsProps = {},
 }: FormItemRowProp<T>) {
  const { isRow } = useFormConfigStore();
  if (!renderType) {
    renderType = isRow ? 'row' : 'descriptions';
  }
  const renderItemCols = useMemo(() => {
    const clearGlobalItemProps = clearObject(globalItemProps);
    return itemCols?.map?.((item) => {
      let itemProps: FormItemProp;
      if (typeof item === 'string') {
        itemProps = {
          ...formItemMap[item],
          ...clearGlobalItemProps,
          formItemKey: item
        } as FormItemProp;
      } else {
        const formItem = clearObject(item) as FormItemProp;
        itemProps = {
          ...formItemMap[formItem.formItemKey],
          ...clearGlobalItemProps,
          ...formItem
        };
      }

      if(renderType === 'descriptions') {
        // 处理Descriptions的单个Item的column，对齐grid布局和Descriptions布局差异
        const column = descriptionsProps.column || 2;
        const singleItemSpan = 24 / column;
        const showItemProps = configFunctionToValue(itemProps as any, renderData);
        let { col, offsetAfter, descriptionItemProp, ...otherProps } = showItemProps as any;
        if (typeof col === 'object') {
          col = col.span;
        }
        return {
          renderData,
          ...otherProps,
          span: col/singleItemSpan || 1,
          offsetAfter: offsetAfter/singleItemSpan || 0,
          ...descriptionItemProp,
        }
      }

      return itemProps
    })
  }, [itemCols, formItemMap, globalItemProps, renderType])

  if(renderType === 'descriptions') {    
    return <DescriptionsFormItem
        {...descriptionsProps}
        items={renderItemCols as any}
    />
  }
  return <Row
      gutter={[24, 0]}
      {...row}
      className={cx('sform-item-row', row?.className)}
  >
    {renderItemCols?.map?.((item, index) => {
      const { formItemKey, ...otherProps } = item
      return (
        <SFormItem
          renderData={renderData}
          key={index}
          labelStyle={{marginBottom: '4px'}}
          formItemKey={formItemKey as string}
          {...otherProps}
        />
      )
    })}
  </Row>
}


export function createFormItemRow<T extends FormItemMapProp = FormItemMapProp>(formItemMap:  Record<string, FormItemProp>) {
  return (props: Omit<FormItemRowProp<T>, 'formItemMap'>) => {
    return <FormItemRow {...props} formItemMap={formItemMap as any}/>
  }
}
